import React from 'react'
import { Modal, Spin, Tag, List, Button, Space } from 'antd'
import {
  CheckCircleOutlined,
  CheckOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  BulbOutlined,
  InfoCircleOutlined,
  SendOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons'
import Style from './preCheckModal.module.scss'

export type PreCheckStatus = 'loading' | 'success' | 'failed'

export interface PreCheckErrorItem {
  label?: string // 前缀标签，加粗展示
  text: string
}

export interface PreCheckModalProps {
  visible: boolean
  status: PreCheckStatus
  errorList?: PreCheckErrorItem[] // 预审失败的错误信息列表
  reviewer?: string // 审核人
  onCancel: () => void
  onModify: () => void // 立即修改
  onProcessLater: () => void // 稍后处理
  onSubmit?: () => void // 确认提交物料（预审成功时）
  submitting?: boolean // 提交进行中：禁用按钮并展示 loading，避免重复提交
}

const PreCheckModal: React.FC<PreCheckModalProps> = ({
  visible,
  status,
  errorList = [],
  reviewer,
  onCancel,
  onModify,
  onProcessLater,
  onSubmit,
  submitting = false,
}) => {
  // 预审中状态
  const renderLoading = () => (
    <div className={Style.content}>
      <div className={Style.section}>
        <div className={Style.sectionHeader}>
          <span className={Style.sectionTitle}>商品预审</span>
          <Tag color="orange" className={Style.statusTag}>
            预审中
          </Tag>
        </div>
        <div className={Style.loadingContent}>
          <Spin size="small" className={Style.loadingIcon} />
          <span className={Style.loadingText}>正在读取项目配置进行校验...</span>
        </div>
      </div>
      <div className={Style.tipsSection}>
        <div className={Style.tipsHeader}>
          <BulbOutlined className={Style.tipsIcon} />
          <span className={Style.tipsTitle}>功能提示</span>
        </div>
        <ul className={Style.tipsList}>
          <li>点击立即修改,相关预警信息将在填写页标红展示处理</li>
          <li>新增操作,基础信息可以直接在当前表单修改</li>
          <li>商品提报操作,基础信息请在我的物料处进行修改</li>
        </ul>
      </div>
      <div className={Style.footer}>
        <Button onClick={onProcessLater} disabled={status === 'loading'}>
          稍后处理
        </Button>
        <Button
          type="primary"
          onClick={onModify}
          disabled={status === 'loading'}
        >
          立即修改
        </Button>
      </div>
    </div>
  )

  // 预审失败状态
  const renderFailed = () => (
    <div className={Style.content}>
      <div className={Style.section}>
        <div className={Style.sectionHeader}>
          <span className={Style.sectionTitle}>商品预审</span>
          <Tag color="red" className={Style.statusTag}>
            预审失败
          </Tag>
        </div>
        <div className={Style.errorList}>
          {errorList.map((error, index) => (
            <div key={index} className={Style.errorItem}>
              <CloseCircleOutlined className={Style.errorIcon} />
              <span className={Style.errorText}>
                {error.label && (
                  <span className={Style.errorLabel}>{error.label}：</span>
                )}
                {error.text}
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className={Style.tipsSection}>
        <div className={Style.tipsHeader}>
          <BulbOutlined className={Style.tipsIcon} />
          <span className={Style.tipsTitle}>功能提示</span>
        </div>
        <ul className={Style.tipsList}>
          <li>点击立即修改,相关预警信息将在填写页标红展示处理</li>
          <li>新增操作,基础信息可以直接在当前表单修改</li>
          <li>商品提报操作,基础信息请在我的物料处进行修改</li>
        </ul>
      </div>
      <div className={Style.footer}>
        <Button onClick={onProcessLater}>稍后处理</Button>
        <Button type="primary" onClick={onModify}>
          立即修改
        </Button>
      </div>
    </div>
  )

  // 预审成功状态
  const renderSuccess = () => (
    <div className={Style.content}>
      <div className={Style.successIconWrapper}>
        <CheckCircleOutlined className={Style.successIcon} />
      </div>
      <div className={Style.successTitle}>预审全部通过</div>
      <div className={Style.successSubtitle}>预审通过,可提交上架申请</div>
      <div className={Style.passedSection}>
        <div className={Style.passedSectionHeader}>
          <UnorderedListOutlined className={Style.listIcon} />
          <span className={Style.passedSectionTitle}>预审通过项</span>
        </div>
        <div className={Style.passedList}>
          {['基础信息校验', '项目信息校验', '项目管控'].map(
            (item, index) => (
              <div key={index} className={Style.passedItem}>
                <div className={Style.passedIconWrapper}>
                  <CheckOutlined className={Style.passedIcon} />
                </div>
                <span className={Style.passedItemText}>{item}</span>
              </div>
            ),
          )}
        </div>
      </div>
      <div className={Style.infoSection}>
        <div className={Style.infoItem}>
          <InfoCircleOutlined className={Style.infoIcon} />
          <span>
            提交后物料将进入运营审核环节,当前审核人为: {reviewer || '-'}
          </span>
        </div>
        <div className={Style.infoItem}>
          <InfoCircleOutlined className={Style.infoIcon} />
          <span>可在「项目提报管理」中查看状态</span>
        </div>
      </div>
      <div className={Style.footer}>
        <Button onClick={onCancel} disabled={submitting}>
          暂不提交
        </Button>
        <Button
          className={Style.submitButton}
          icon={<SendOutlined />}
          onClick={onSubmit}
          loading={submitting}
        >
          确认提交物料
        </Button>
      </div>
    </div>
  )

  return (
    <Modal
      title="物料预审结果"
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={450}
      centered
      maskClosable={false}
      closable={!submitting}
      className={Style.modal}
    >
      {status === 'loading' && renderLoading()}
      {status === 'failed' && renderFailed()}
      {status === 'success' && renderSuccess()}
    </Modal>
  )
}

export default PreCheckModal
import React from 'react'
import { Modal, Spin, Tag, List, Button, Space } from 'antd'
import {
  CheckCircleOutlined,
  CheckOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  BulbOutlined,
  InfoCircleOutlined,
  SendOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons'
import Style from './preCheckModal.module.scss'

export type PreCheckStatus = 'loading' | 'success' | 'failed'

export interface PreCheckErrorItem {
  label?: string // 前缀标签，加粗展示
  text: string
}

export interface PreCheckModalProps {
  visible: boolean
  status: PreCheckStatus
  errorList?: PreCheckErrorItem[] // 预审失败的错误信息列表
  reviewer?: string // 审核人
  onCancel: () => void
  onModify: () => void // 立即修改
  onProcessLater: () => void // 稍后处理
  onSubmit?: () => void // 确认提交物料（预审成功时）
  submitting?: boolean // 提交进行中：禁用按钮并展示 loading，避免重复提交
}

const PreCheckModal: React.FC<PreCheckModalProps> = ({
  visible,
  status,
  errorList = [],
  reviewer,
  onCancel,
  onModify,
  onProcessLater,
  onSubmit,
  submitting = false,
}) => {
  // 预审中状态
  const renderLoading = () => (
    <div className={Style.content}>
      <div className={Style.section}>
        <div className={Style.sectionHeader}>
          <span className={Style.sectionTitle}>商品预审</span>
          <Tag color="orange" className={Style.statusTag}>
            预审中
          </Tag>
        </div>
        <div className={Style.loadingContent}>
          <Spin size="small" className={Style.loadingIcon} />
          <span className={Style.loadingText}>正在读取项目配置进行校验...</span>
        </div>
      </div>
      <div className={Style.tipsSection}>
        <div className={Style.tipsHeader}>
          <BulbOutlined className={Style.tipsIcon} />
          <span className={Style.tipsTitle}>功能提示</span>
        </div>
        <ul className={Style.tipsList}>
          <li>点击立即修改,相关预警信息将在填写页标红展示处理</li>
          <li>新增操作,基础信息可以直接在当前表单修改</li>
          <li>商品提报操作,基础信息请在我的物料处进行修改</li>
        </ul>
      </div>
      <div className={Style.footer}>
        <Button onClick={onProcessLater} disabled={status === 'loading'}>
          稍后处理
        </Button>
        <Button
          type="primary"
          onClick={onModify}
          disabled={status === 'loading'}
        >
          立即修改
        </Button>
      </div>
    </div>
  )

  // 预审失败状态
  const renderFailed = () => (
    <div className={Style.content}>
      <div className={Style.section}>
        <div className={Style.sectionHeader}>
          <span className={Style.sectionTitle}>商品预审</span>
          <Tag color="red" className={Style.statusTag}>
            预审失败
          </Tag>
        </div>
        <div className={Style.errorList}>
          {errorList.map((error, index) => (
            <div key={index} className={Style.errorItem}>
              <CloseCircleOutlined className={Style.errorIcon} />
              <span className={Style.errorText}>
                {error.label && (
                  <span className={Style.errorLabel}>{error.label}：</span>
                )}
                {error.text}
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className={Style.tipsSection}>
        <div className={Style.tipsHeader}>
          <BulbOutlined className={Style.tipsIcon} />
          <span className={Style.tipsTitle}>功能提示</span>
        </div>
        <ul className={Style.tipsList}>
          <li>点击立即修改,相关预警信息将在填写页标红展示处理</li>
          <li>新增操作,基础信息可以直接在当前表单修改</li>
          <li>商品提报操作,基础信息请在我的物料处进行修改</li>
        </ul>
      </div>
      <div className={Style.footer}>
        <Button onClick={onProcessLater}>稍后处理</Button>
        <Button type="primary" onClick={onModify}>
          立即修改
        </Button>
      </div>
    </div>
  )

  // 预审成功状态
  const renderSuccess = () => (
    <div className={Style.content}>
      <div className={Style.successIconWrapper}>
        <CheckCircleOutlined className={Style.successIcon} />
      </div>
      <div className={Style.successTitle}>预审全部通过</div>
      <div className={Style.successSubtitle}>预审通过,可提交上架申请</div>
      <div className={Style.passedSection}>
        <div className={Style.passedSectionHeader}>
          <UnorderedListOutlined className={Style.listIcon} />
          <span className={Style.passedSectionTitle}>预审通过项</span>
        </div>
        <div className={Style.passedList}>
          {['基础信息校验', '项目信息校验', '项目管控'].map(
            (item, index) => (
              <div key={index} className={Style.passedItem}>
                <div className={Style.passedIconWrapper}>
                  <CheckOutlined className={Style.passedIcon} />
                </div>
                <span className={Style.passedItemText}>{item}</span>
              </div>
            ),
          )}
        </div>
      </div>
      <div className={Style.infoSection}>
        <div className={Style.infoItem}>
          <InfoCircleOutlined className={Style.infoIcon} />
          <span>
            提交后物料将进入运营审核环节,当前审核人为: {reviewer || '-'}
          </span>
        </div>
        <div className={Style.infoItem}>
          <InfoCircleOutlined className={Style.infoIcon} />
          <span>可在「项目提报管理」中查看状态</span>
        </div>
      </div>
      <div className={Style.footer}>
        <Button onClick={onCancel} disabled={submitting}>
          暂不提交
        </Button>
        <Button
          className={Style.submitButton}
          icon={<SendOutlined />}
          onClick={onSubmit}
          loading={submitting}
        >
          确认提交物料
        </Button>
      </div>
    </div>
  )

  return (
    <Modal
      title="物料预审结果"
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={450}
      centered
      maskClosable={false}
      closable={!submitting}
      className={Style.modal}
    >
      {status === 'loading' && renderLoading()}
      {status === 'failed' && renderFailed()}
      {status === 'success' && renderSuccess()}
    </Modal>
  )
}

export default PreCheckModal
